import React from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useParams } from "react-router";

import { AppBreadcrumbs } from "@shell/components/AppBreadcrumbs";
import { getContainerStyles } from "@shell/styles/themeUtils";
import {
  ORGANIZATIONS,
  PLATFORM_PRODUCTS,
} from "src/platform/constants/routes";
import { useAppNavigation } from "src/platform/hooks/useAppNavigation";

export type ProductDetailsViewState =
  "loaded" | "loading" | "error" | "forbidden";

const LLM_GATEWAY = {
  key: "llm-gateway",
  name: "LLM Gateway",
  category: "AI infrastructure",
  description:
    "A managed, organization-scoped gateway for routing application requests to approved language-model backends.",
  capabilities: [
    "OpenAI-compatible request shape",
    "Organization-scoped routing",
    "Usage visibility",
  ],
};

export interface PlatformProductDetailsPageProps {
  /** Optional deterministic state hook for route and state coverage. */
  viewState?: ProductDetailsViewState;
}

const PlatformProductDetailsPage: React.FC<PlatformProductDetailsPageProps> = ({
  viewState = "loaded",
}) => {
  const theme = useTheme();
  const appNavigation = useAppNavigation();
  const { orgAlias, productKey } = useParams<{
    orgAlias: string;
    productKey: string;
  }>();

  if (!orgAlias || !productKey) return null;

  const backToCatalog = () => appNavigation.goToPlatformProducts(orgAlias);
  const breadcrumbs = (
    <AppBreadcrumbs
      items={[
        { label: "Organizations", to: ORGANIZATIONS.LIST.path },
        { label: orgAlias, to: ORGANIZATIONS.DETAILS.getPath(orgAlias) },
        {
          label: PLATFORM_PRODUCTS.CATALOG.title,
          to: PLATFORM_PRODUCTS.CATALOG.getPath(orgAlias),
        },
        { label: "Product details" },
      ]}
    />
  );

  if (viewState === "loading") {
    return (
      <Container sx={getContainerStyles(theme)}>
        {breadcrumbs}
        <Stack
          spacing={2}
          sx={{ mt: 3 }}
          role="status"
          aria-label="Loading product details"
        >
          <Skeleton variant="text" width={280} height={42} />
          <Skeleton variant="text" width={560} height={24} />
          <Skeleton variant="rounded" height={220} />
        </Stack>
      </Container>
    );
  }

  if (viewState === "error") {
    return (
      <Container sx={getContainerStyles(theme)}>
        {breadcrumbs}
        <StateCard title="Product details unavailable" onBack={backToCatalog}>
          Product details could not be loaded. Refresh the page to try again.
        </StateCard>
      </Container>
    );
  }

  if (viewState === "forbidden") {
    return (
      <Container sx={getContainerStyles(theme)}>
        {breadcrumbs}
        <StateCard title="Permission required" onBack={backToCatalog}>
          You do not have permission to view product details for this
          organization.
        </StateCard>
      </Container>
    );
  }

  if (productKey !== LLM_GATEWAY.key) {
    return (
      <Container sx={getContainerStyles(theme)}>
        {breadcrumbs}
        <StateCard title="Product details unavailable" onBack={backToCatalog}>
          This product does not have a read-only details destination in the
          current release.
        </StateCard>
      </Container>
    );
  }

  return (
    <Container sx={getContainerStyles(theme)}>
      {breadcrumbs}
      <Stack spacing={3}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Stack spacing={1}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <ProductMark />
              <Typography variant="h4" component="h1" fontWeight={700}>
                {LLM_GATEWAY.name}
              </Typography>
              <Chip
                label="Read-only"
                color="info"
                variant="outlined"
                size="small"
              />
            </Stack>
            <Typography variant="body1" color="text.secondary" maxWidth={720}>
              {LLM_GATEWAY.description}
            </Typography>
          </Stack>
          <Stack spacing={0.5} sx={{ flexShrink: 0 }}>
            <DetailRow label="Organization" value={orgAlias} />
            <DetailRow label="Product status" value="Details only" />
          </Stack>
        </Stack>

        <Alert severity="info">
          This destination provides product information only. Product
          activation, management, and runtime configuration are handled by
          separate releases.
        </Alert>

        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Gateway overview
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  LLM Gateway provides a consistent interface for approved model
                  backends while keeping product context scoped to {orgAlias}.
                </Typography>
                <Stack spacing={1.5}>
                  <DetailRow label="Category" value={LLM_GATEWAY.category} />
                  <DetailRow label="Scope" value="Organization" />
                  <DetailRow label="Availability" value="Catalog discovery" />
                  <DetailRow label="Current surface" value="Read-only" />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={5}>
            <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Capabilities
                </Typography>
                <Stack spacing={1}>
                  {LLM_GATEWAY.capabilities.map((capability) => (
                    <Typography
                      key={capability}
                      variant="body2"
                      color="text.secondary"
                    >
                      {capability}
                    </Typography>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Organization boundary
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This page does not create, change, or retrieve product resources.
              Return to the catalog to review other read-only product entries.
            </Typography>
          </CardContent>
        </Card>

        <Box>
          <Button
            variant="outlined"
            onClick={backToCatalog}
            sx={{ textTransform: "none" }}
          >
            Back to products
          </Button>
        </Box>
      </Stack>
    </Container>
  );
};

const ProductMark: React.FC = () => (
  <Box
    aria-hidden
    sx={{
      width: 44,
      height: 44,
      borderRadius: 1.5,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "1.25rem",
      bgcolor: "rgba(37, 99, 235, 0.2)",
      border: "1px solid rgba(96, 165, 250, 0.35)",
      color: "#bfdbfe",
      flexShrink: 0,
    }}
  >
    ✦
  </Box>
);

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Stack
    direction="row"
    spacing={2}
    justifyContent="space-between"
    alignItems="center"
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography
      component="div"
      variant="body2"
      fontWeight={600}
      textAlign="right"
    >
      {value}
    </Typography>
  </Stack>
);

const StateCard: React.FC<{
  title: string;
  onBack: () => void;
  children: React.ReactNode;
}> = ({ title, onBack, children }) => (
  <Card variant="outlined" sx={{ borderRadius: 2, mt: 3 }}>
    <CardContent>
      <Stack
        spacing={2}
        alignItems="center"
        sx={{ textAlign: "center", py: 4 }}
      >
        <Typography variant="h5" fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" maxWidth={480}>
          {children}
        </Typography>
        <Button
          variant="outlined"
          onClick={onBack}
          sx={{ textTransform: "none" }}
        >
          Back to product catalog
        </Button>
      </Stack>
    </CardContent>
  </Card>
);

export default PlatformProductDetailsPage;
